import { useState, useEffect, useCallback } from 'react'
import { 
  Briefcase, FileText, MessageSquare, User, ArrowLeft, Loader2, 
  Upload, CheckCircle, Clock, FileCheck, LogOut 
} from 'lucide-react'
import ChatPanel from '../components/ChatPanel'
import { chatApi } from '../lib/chatApi'
import { agentRequestsApi } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import Sidebar from '../components/layout/Sidebar'
import Header from '../components/layout/Header'
import { AGENT_NAV } from '../data/nav'

const fmtDate = (iso) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

export default function AgentDashboard({ activeTab = 'projects', onNavigate }) {
  const { user, profile, signOut } = useAuth()
  const agentId = user?.id
  const [conversations, setConversations] = useState([])
  const [activeConv, setActiveConv] = useState(null)
  const [assignments, setAssignments] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploadingFor, setUploadingFor] = useState(null)
  const [mobileOpen, setMobileOpen] = useState(false)

  // Map activeTab to route for sidebar
  const getRoute = () => {
    if (activeTab === 'agent-projects') return 'agent-projects'
    if (activeTab === 'agent-chat') return 'agent-chat'
    if (activeTab === 'agent-profile') return 'agent-profile'
    if (activeTab === 'agent-upload') return 'agent-upload'
    return 'agent-projects'
  }

  const loadData = useCallback(async () => {
    if (!agentId) { setLoading(false); return }
    const [convResult, reqResult] = await Promise.all([
      chatApi.listConversations(agentId, 'agent'),
      supabase.from('agent_requests').select('*, companies!inner(name)').eq('lawyer_id', agentId).order('created_at', { ascending: false }),
    ])
    setConversations(convResult.data || [])
    setAssignments(reqResult.data || [])
    setLoading(false)
  }, [agentId])

  useEffect(() => {
    loadData()
    const interval = setInterval(loadData, 15000)
    return () => clearInterval(interval)
  }, [loadData])

  const handleLogout = () => {
    signOut?.()
    onNavigate?.('login')
  }

  const handleNavigate = (route) => {
    if (route === 'agent-projects' || route === 'agent-chat' || route === 'agent-profile' || route === 'agent-upload') {
      onNavigate?.(route)
    } else {
      onNavigate?.(route)
    }
    setMobileOpen(false)
  }

  const handleProfileClick = () => {
    onNavigate?.('agent-profile')
    setMobileOpen(false)
  }

  // Stats
  const stats = {
    total: assignments.length,
    active: assignments.filter(a => a.status !== 'Finalized').length,
    completed: assignments.filter(a => a.status === 'Finalized').length,
  }

  // ============================================================
  // RENDER CONTENT BASED ON ACTIVE TAB
  // ============================================================

  const renderContent = () => {
    if (activeTab === 'agent-chat') {
      return (
        <AgentChatView 
          conversations={conversations} 
          activeConv={activeConv}
          setActiveConv={setActiveConv}
          onBack={() => onNavigate?.('agent-projects')}
        />
      )
    }

    if (activeTab === 'agent-profile') {
      return (
        <AgentProfileView 
          profile={profile} 
          user={user} 
          onBack={() => onNavigate?.('agent-projects')} 
        />
      )
    }

    if (activeTab === 'agent-upload') {
      return (
        <AgentUploadPortal 
          assignments={assignments} 
          onNavigate={onNavigate} 
          uploadingFor={uploadingFor} 
          setUploadingFor={setUploadingFor} 
          agentId={agentId} 
        />
      )
    }

    // Default: project tracking
    return (
      <ProjectsView 
        assignments={assignments}
        loading={loading}
        stats={stats}
        onNavigate={onNavigate}
        setUploadingFor={setUploadingFor}
      />
    )
  }

  return (
    <div className="h-full flex">
      {/* Sidebar */}
      <Sidebar
        nav={AGENT_NAV}
        activeTab={getRoute()}
        onSelect={handleNavigate}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <Header
          company={null}
          user={user}
          profile={profile}
          role="agent"
          onMenu={() => setMobileOpen(true)}
          onLogout={handleLogout}
          onProfileClick={handleProfileClick}
          onNavigate={handleNavigate}
        />
        <main className="flex-1 overflow-y-auto bg-neutral-50 p-4 sm:p-6 lg:p-8">
          {renderContent()}
        </main>
      </div>
    </div>
  )
}

// ============================================================
// PROJECTS VIEW
// ============================================================

function ProjectsView({ assignments, loading, stats, onNavigate, setUploadingFor }) {
  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-ink flex items-center gap-2">
          <Briefcase size={22} /> Project Tracking
        </h1>
        <p className="text-sm text-muted mt-1">Active and historical assignments.</p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-neutral-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-ink">{stats.total}</p>
          <p className="text-xs text-muted">Total Projects</p>
        </div>
        <div className="bg-white border border-neutral-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-amber-600">{stats.active}</p>
          <p className="text-xs text-muted">Active</p>
        </div>
        <div className="bg-white border border-neutral-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-emerald-600">{stats.completed}</p>
          <p className="text-xs text-muted">Completed</p>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 size={20} className="animate-spin text-neutral-400" />
        </div>
      )}

      {!loading && assignments.length === 0 && (
        <div className="bg-white border border-neutral-200 rounded-xl p-10 text-center">
          <Briefcase size={32} className="text-neutral-300 mx-auto mb-4" />
          <p className="text-sm font-semibold text-ink">No assignments yet</p>
          <p className="text-xs text-muted mt-1">Projects assigned to you will appear here.</p>
        </div>
      )}

      {!loading && assignments.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[600px]">
              <div className="grid grid-cols-12 px-6 py-3 border-b border-neutral-200 text-[11px] font-semibold uppercase tracking-[0.1em] text-neutral-500">
                <div className="col-span-3">Company</div>
                <div className="col-span-3">Document</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Assigned</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              <div className="divide-y divide-neutral-200">
                {assignments.map((a) => (
                  <div key={a.id} className="grid grid-cols-12 px-6 py-4 text-sm items-center">
                    <div className="col-span-3 text-ink font-medium">{a.companies?.name || '—'}</div>
                    <div className="col-span-3 text-neutral-600">{a.document_name}</div>
                    <div className="col-span-2">
                      <StatusBadge status={a.status} />
                    </div>
                    <div className="col-span-2 text-xs text-neutral-500 font-mono">{fmtDate(a.assigned_date)}</div>
                    <div className="col-span-2 flex justify-end gap-2">
                      <button 
                        onClick={() => onNavigate?.('agent-chat')} 
                        className="text-neutral-400 hover:text-brand transition-colors p-1.5"
                        title="Chat"
                      >
                        <MessageSquare size={16} />
                      </button>
                      <button 
                        onClick={() => { setUploadingFor(a); onNavigate?.('agent-upload') }} 
                        className="text-neutral-400 hover:text-brand transition-colors p-1.5"
                        title="Upload Draft"
                      >
                        <Upload size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================
// AGENT CHAT VIEW
// ============================================================

function AgentChatView({ conversations, activeConv, setActiveConv, onBack }) {
  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center gap-2">
        <button onClick={onBack} className="btn-ghost text-xs flex items-center gap-1.5">
          <ArrowLeft size={14} /> Back to Projects
        </button>
        <h1 className="text-2xl font-bold text-ink">Founder Chat</h1>
      </div>
      <p className="text-sm text-muted">Message founders whose requests you have accepted.</p>

      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 h-[calc(100vh-280px)] min-h-[400px]">
        <div className="w-full lg:w-80 shrink-0 bg-white border border-neutral-200 rounded-xl flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-neutral-200">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted">
              Conversations ({conversations.length})
            </p>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="text-center py-10 px-4">
                <MessageSquare size={28} className="text-neutral-300 mx-auto mb-3" />
                <p className="text-sm font-semibold text-ink">No conversations</p>
                <p className="text-xs text-muted mt-1">Accept a request to start chatting with a founder.</p>
              </div>
            ) : (
              conversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => setActiveConv(conv)}
                  className={`w-full px-4 py-3 text-left border-b border-neutral-100 hover:bg-neutral-50 transition-colors ${
                    activeConv?.id === conv.id ? 'bg-brand-soft' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-ink truncate">Founder</span>
                    <span className="text-[10px] text-muted">{fmtDate(conv.created_at)}</span>
                  </div>
                  <p className="text-xs text-muted truncate">{conv.agent_email || '—'}</p>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="flex-1 bg-white border border-neutral-200 rounded-xl overflow-hidden">
          {activeConv ? (
            <ChatPanel 
              conversationId={activeConv.id} 
              onBack={() => setActiveConv(null)} 
              agentEmail={activeConv.agent_email} 
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center">
              <MessageSquare size={28} className="text-neutral-400 mb-3" />
              <p className="text-sm text-muted">Select a conversation to start messaging.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================
// AGENT PROFILE VIEW
// ============================================================

function AgentProfileView({ profile, user, onBack }) {
  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center gap-2">
        <button onClick={onBack} className="btn-ghost text-xs flex items-center gap-1.5">
          <ArrowLeft size={14} /> Back to Projects
        </button>
        <h1 className="text-2xl font-bold text-ink">Agent Profile</h1>
      </div>
      <p className="text-sm text-muted">Your professional profile visible to founders.</p>

      <div className="bg-white border border-neutral-200 rounded-xl p-6 max-w-2xl">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-brand-soft rounded-full flex items-center justify-center">
            <User size={28} className="text-brand" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-ink">{profile?.full_name || user?.email || 'Agent'}</h2>
            <p className="text-sm text-muted">{user?.email}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label-above">Full Name</label>
            <p className="text-sm text-ink">{profile?.full_name || 'Not set'}</p>
          </div>
          <div>
            <label className="label-above">Phone</label>
            <p className="text-sm text-ink">{profile?.phone || 'Not set'}</p>
          </div>
          <div>
            <label className="label-above">Firm / Agency</label>
            <p className="text-sm text-ink">{profile?.firm || 'Not set'}</p>
          </div>
          <div>
            <label className="label-above">Country</label>
            <p className="text-sm text-ink">{profile?.country || 'Not set'}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// AGENT UPLOAD PORTAL
// ============================================================

function AgentUploadPortal({ assignments, onNavigate, uploadingFor, setUploadingFor, agentId }) {
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)

  const handleUpload = async (e) => {
    e.preventDefault()
    if (!file || !uploadingFor) return
    setUploading(true)
    setError(null)
    setSuccess(false)
    try {
      const { error: updateError } = await agentRequestsApi.uploadDraft(uploadingFor.id)
      if (updateError) throw updateError
      setSuccess(true)
      setFile(null)
    } catch (err) {
      setError(err.message || 'Upload failed')
    }
    setUploading(false)
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center gap-2">
        <button onClick={() => onNavigate?.('agent-projects')} className="btn-ghost text-xs flex items-center gap-1.5">
          <ArrowLeft size={14} /> Back to Projects
        </button>
        <h1 className="text-2xl font-bold text-ink flex items-center gap-2">
          <Upload size={22} /> Document Upload Portal
        </h1>
      </div>
      <p className="text-sm text-muted">Upload completed documents and send them to the requesting founder.</p>

      <div className="bg-white border border-neutral-200 rounded-xl p-6 max-w-2xl">
        {!uploadingFor && (
          <div className="text-center py-8">
            <FileCheck size={32} className="text-neutral-300 mx-auto mb-4" />
            <p className="text-sm font-semibold text-ink">Select a project to upload</p>
            <p className="text-xs text-muted mt-1">Go to Project Tracking and click the upload icon for a specific assignment.</p>
            <button onClick={() => onNavigate?.('agent-projects')} className="btn-secondary text-xs mt-4">Go to Projects</button>
          </div>
        )}

        {uploadingFor && (
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="border border-neutral-200 bg-neutral-50 px-4 py-3 rounded-lg">
              <p className="text-xs text-muted">Uploading for</p>
              <p className="text-sm font-semibold text-ink">{uploadingFor.companies?.name || '—'}</p>
              <p className="text-xs text-neutral-600">{uploadingFor.document_name}</p>
            </div>
            <div>
              <label className="label-above">Document File</label>
              <input
                type="file"
                accept="image/*,.pdf,.doc,.docx"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="input-flat"
                required
              />
            </div>
            {error && <p className="text-xs text-rose-600">{error}</p>}
            {success && (
              <div className="flex items-center gap-2 text-xs text-emerald-700">
                <CheckCircle size={14} /> Draft uploaded successfully. The founder has been notified.
              </div>
            )}
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={uploading || !file}>
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              Upload & Send to Founder
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

// ============================================================
// STATUS BADGE
// ============================================================

function StatusBadge({ status }) {
  const map = {
    Assigned: { cls: 'text-blue-700 bg-blue-50 border-blue-200', icon: Clock },
    Drafting: { cls: 'text-purple-700 bg-purple-50 border-purple-200', icon: FileText },
    'Client Review': { cls: 'text-amber-700 bg-amber-50 border-amber-200', icon: FileText },
    Finalized: { cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', icon: CheckCircle },
    Open: { cls: 'text-neutral-600 bg-neutral-50 border-neutral-200', icon: Clock },
  }
  const cfg = map[status] || map.Open
  const Icon = cfg.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold border rounded-full ${cfg.cls}`}>
      <Icon size={10} /> {status}
    </span>
  )
}